import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Settings as SettingsIcon } from "lucide-react";
import { adminApi, setWhatsAppNumber } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-white dark:bg-emerald-900/60 border border-slate-200 dark:border-emerald-800 text-sm text-slate-800 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

export const AdminSettings = () => {
  const { settings, setSettings } = useApp();
  const [number, setNumber] = useState("");
  const [packages, setPackages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setNumber(settings.whatsapp_number || "");
      setPackages(settings.packages || []);
    }
  }, [settings]);

  const setPkg = (i, k) => (e) => setPackages((ps) => ps.map((p, idx) => (idx === i ? { ...p, [k]: k === "price" ? Number(e.target.value) : e.target.value } : p)));

  const save = async () => {
    if (number.replace(/\D/g, "").length < 10) {
      toast.error("WhatsApp number must have at least 10 digits (with country code)");
      return;
    }
    if (packages.some((p) => !p.name.trim() || !(p.price > 0))) {
      toast.error("Each package needs a name and a price above 0");
      return;
    }
    setSaving(true);
    try {
      const res = await adminApi.put("/settings", { whatsapp_number: number, packages });
      setSettings(res.data);
      setWhatsAppNumber(res.data.whatsapp_number);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 mt-6" data-testid="admin-settings">
      <div className="flex items-center gap-2 mb-3">
        <SettingsIcon className="w-4 h-4 text-emerald-700 dark:text-emerald-300" />
        <h3 className="text-base font-bold text-emerald-950 dark:text-emerald-50 font-head">Booking Settings</h3>
      </div>
      <div className="rounded-2xl bg-white dark:bg-emerald-900/60 border border-slate-100 dark:border-emerald-800 p-4 space-y-4">
        <label className="block">
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400">WhatsApp number (with country code)</span>
          <input data-testid="settings-whatsapp-input" value={number} onChange={(e) => setNumber(e.target.value)} inputMode="tel" placeholder="918637522628" className={`${inputCls} mt-1`} />
          <span className="text-[11px] text-slate-400">Used for bookings, product orders and replies.</span>
        </label>

        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">Consultation packages</p>
          <div className="space-y-3">
            {packages.map((p, i) => (
              <div key={p.id} data-testid={`settings-package-${p.id}`} className="p-3 rounded-xl border border-slate-100 dark:border-emerald-800 bg-slate-50/60 dark:bg-emerald-950/30 space-y-2">
                <input data-testid={`settings-package-name-${p.id}`} value={p.name} onChange={setPkg(i, "name")} placeholder="Package name" className={inputCls} />
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid={`settings-package-price-${p.id}`} type="number" inputMode="numeric" value={p.price} onChange={setPkg(i, "price")} placeholder="Price (₹)" className={inputCls} />
                  <input data-testid={`settings-package-duration-${p.id}`} value={p.duration} onChange={setPkg(i, "duration")} placeholder="Duration" className={inputCls} />
                </div>
                <input data-testid={`settings-package-desc-${p.id}`} value={p.description} onChange={setPkg(i, "description")} placeholder="Short description" className={inputCls} />
              </div>
            ))}
          </div>
        </div>

        <button
          data-testid="settings-save-btn"
          onClick={save}
          disabled={saving}
          className="w-full flex items-center justify-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 rounded-2xl transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Settings
        </button>
      </div>
    </div>
  );
};
